import * as Sentry from '@sentry/browser';
import { sentryErrorIntegration, ignoreUserAgentIntegration } from '@ifixit/sentry';

const ERROR_SAMPLE_RATE_PRODUCTION = 0.05;
const ERROR_SAMPLE_RATE_DEV = 0;

const TRACE_SAMPLE_RATE = App.isProduction ? 0.005 : 0;

const errorSampleRate = App.isProduction ? ERROR_SAMPLE_RATE_PRODUCTION : ERROR_SAMPLE_RATE_DEV;

let errorsLeftToSend = 1;
let iconReportsLeftToSend = 1;
const forceSentry = App.canIForceSentryReporting && /(forceSentry)/.test(window.location.search);
const performanceOptions = App.sentryPerformance
   ? {
        integrations: [
           Sentry.browserTracingIntegration(),
           sentryErrorIntegration(),
           ignoreUserAgentIntegration(),
           Sentry.thirdPartyErrorFilterIntegration({
              filterKeys: ['ifixitFirstParty'],
              behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
           }),
        ],
        tracesSampleRate: TRACE_SAMPLE_RATE,
     }
   : {
        integrations: [
           sentryErrorIntegration(),
           ignoreUserAgentIntegration(),
           Sentry.thirdPartyErrorFilterIntegration({
              filterKeys: ['ifixitFirstParty'],
              behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
           }),
        ],
     };

let sampleRate = forceSentry ? 1 : errorSampleRate;
let shouldSendToSentry = Math.random() < sampleRate;

Sentry.init({
   dsn: App.SENTRY_DSN_JS,
   environment: App.isProduction ? 'production' : 'dev',
   release: App.sentryRelease,
   ignoreErrors: [
      'TypeError: Network request failed',
      // Lazy chunk fetch failed (network drop, blocked request, etc.); not
      // actionable from app code.
      'ChunkLoadError',
      /Loading (CSS )?chunk \S+ failed/i,
   ],
   ...performanceOptions,

   // We effect our sampling in beforeSend, so we tell sentry to sample all
   // errors
   sampleRate: 1,
   beforeSend: function (event) {
      // Sample-exempt events still respect the dev rate of 0, so a dev
      // page stays silent without ?forceSentry.
      if (event.tags && event.tags.bypassErrorSampling && sampleRate > 0) {
         return event;
      }

      if (!shouldSendToSentry) {
         return null;
      }

      // Icon diagnostics fire before app code can throw; a shared counter
      // would let them spend the one real-error slot every time.
      if (event.tags && event.tags.iconDiagnostic) {
         if (iconReportsLeftToSend <= 0) {
            return null;
         }
         iconReportsLeftToSend--;
         return event;
      }

      if (errorsLeftToSend <= 0) {
         return null;
      }
      errorsLeftToSend--;
      return event;
   },
   normalizeDepth: 10,
});

Sentry.setTags(App.sentryTags);
Sentry.setUser({ ip_address: '{{auto}}' });

// An icon href the CDN lacks (an FA4 name passed through) fails only in the
// browser. `error` doesn't bubble, but the capture phase descends to target.
const reportedUseHrefs = new Set();
// #63967: clients that block or abort the sprite fetch fire this for a
// sprite that serves fine; hold the sends until this can verify the href.
const sendFailedUseHrefs = false;
function reportFailedUseHref(href) {
   if (!sendFailedUseHrefs || reportedUseHrefs.has(href)) {
      return;
   }
   reportedUseHrefs.add(href);
   Sentry.captureMessage(`svg <use> failed to load: ${href}`, {
      level: 'warning',
      tags: { iconDiagnostic: 'true' },
   });
}
document.addEventListener(
   'error',
   ev => {
      if (ev.target instanceof SVGUseElement) {
         reportFailedUseHref(ev.target.getAttribute('href') || '(no href)');
      }
   },
   { capture: true }
);
// A same-origin 404 beats this deferred bundle even on a small page, so
// SvgUseErrorQueueComponent's inline head script queues early failures.
(window.__failedSvgUseHrefs || []).forEach(reportFailedUseHref);
// Drained once; drop the inline listener so it stops refilling the queue.
if (window.__svgUseErrorListener) {
   document.removeEventListener('error', window.__svgUseErrorListener, true);
}
